import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

type DialogTone = 'default' | 'danger' | 'success';

type DialogOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
};

type PendingDialog = DialogOptions & {
  kind: 'confirmation' | 'notice';
  returnFocusTo: HTMLElement | null;
  resolve: (confirmed: boolean) => void;
};

type AppDialogContextValue = {
  requestConfirmation: (options: DialogOptions) => Promise<boolean>;
  showNotice: (options: DialogOptions) => Promise<void>;
};

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

const toneStyles: Record<DialogTone, {
  icon: typeof Info;
  iconClassName: string;
  buttonClassName: string;
}> = {
  default: {
    icon: Info,
    iconClassName: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
    buttonClassName: 'bg-cyan-600 text-white hover:bg-cyan-500 focus-visible:outline-cyan-500',
  },
  danger: {
    icon: AlertTriangle,
    iconClassName: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    buttonClassName: 'bg-rose-600 text-white hover:bg-rose-500 focus-visible:outline-rose-500',
  },
  success: {
    icon: CheckCircle2,
    iconClassName: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    buttonClassName: 'bg-emerald-600 text-white hover:bg-emerald-500 focus-visible:outline-emerald-500',
  },
};

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    )
  );
}

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [activeDialog, setActiveDialog] = useState<PendingDialog | null>(null);
  const activeDialogRef = useRef<PendingDialog | null>(null);
  const queueRef = useRef<PendingDialog[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const enqueue = useCallback((dialog: PendingDialog) => {
    if (activeDialogRef.current) {
      queueRef.current.push(dialog);
      return;
    }

    activeDialogRef.current = dialog;
    setActiveDialog(dialog);
  }, []);

  const requestConfirmation = useCallback(
    (options: DialogOptions) =>
      new Promise<boolean>((resolve) => {
        enqueue({
          ...options,
          kind: 'confirmation',
          returnFocusTo: document.activeElement instanceof HTMLElement ? document.activeElement : null,
          resolve,
        });
      }),
    [enqueue]
  );

  const showNotice = useCallback(
    (options: DialogOptions) =>
      new Promise<void>((resolve) => {
        enqueue({
          ...options,
          kind: 'notice',
          returnFocusTo: document.activeElement instanceof HTMLElement ? document.activeElement : null,
          resolve: () => resolve(),
        });
      }),
    [enqueue]
  );

  const settleDialog = useCallback((confirmed: boolean) => {
    const settledDialog = activeDialogRef.current;
    if (!settledDialog) {
      return;
    }

    const nextDialog = queueRef.current.shift() ?? null;
    activeDialogRef.current = nextDialog;
    setActiveDialog(nextDialog);
    settledDialog.resolve(confirmed);

    if (!nextDialog) {
      window.setTimeout(() => settledDialog.returnFocusTo?.focus(), 0);
    }
  }, []);

  useEffect(() => {
    if (!activeDialog) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTarget = activeDialog.kind === 'confirmation'
      ? cancelButtonRef.current
      : confirmButtonRef.current;
    focusTarget?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeDialog]);

  useEffect(() => () => {
    activeDialogRef.current?.resolve(false);
    queueRef.current.forEach((dialog) => dialog.resolve(false));
    queueRef.current = [];
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      settleDialog(false);
      return;
    }

    if (event.key !== 'Tab' || !dialogRef.current) {
      return;
    }

    const focusable = focusableElements(dialogRef.current);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const contextValue = { requestConfirmation, showNotice };
  const tone = activeDialog?.tone ?? 'default';
  const toneStyle = toneStyles[tone];
  const ToneIcon = toneStyle.icon;

  return (
    <AppDialogContext.Provider value={contextValue}>
      {children}
      {activeDialog
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  settleDialog(false);
                }
              }}
            >
              <div
                ref={dialogRef}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                onKeyDown={handleKeyDown}
                className="w-full max-w-md overflow-hidden rounded-lg border border-slate-300 bg-white shadow-2xl shadow-slate-950/30 dark:border-slate-700 dark:bg-slate-950 dark:shadow-slate-950/70"
              >
                <div className="flex items-start gap-4 px-5 pb-4 pt-5">
                  <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${toneStyle.iconClassName}`}>
                    <ToneIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 id={titleId} className="text-base font-semibold text-slate-950 dark:text-slate-100">
                      {activeDialog.title}
                    </h2>
                    <p
                      id={descriptionId}
                      className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-400"
                    >
                      {activeDialog.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => settleDialog(false)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                    aria-label="Close dialog"
                    title="Close"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/70">
                  {activeDialog.kind === 'confirmation' ? (
                    <button
                      ref={cancelButtonRef}
                      type="button"
                      onClick={() => settleDialog(false)}
                      className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {activeDialog.cancelLabel ?? 'Cancel'}
                    </button>
                  ) : null}
                  <button
                    ref={confirmButtonRef}
                    type="button"
                    onClick={() => settleDialog(true)}
                    className={`rounded-md px-4 py-2.5 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${toneStyle.buttonClassName}`}
                  >
                    {activeDialog.confirmLabel ?? (activeDialog.kind === 'notice' ? 'OK' : 'Confirm')}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </AppDialogContext.Provider>
  );
}

export function useAppDialog(): AppDialogContextValue {
  const context = useContext(AppDialogContext);
  if (!context) {
    throw new Error('useAppDialog must be used within AppDialogProvider');
  }
  return context;
}
